/* header.js — KOSHO SG site header behavior.
 * Exposes window.KoshoHeader = {
 *   init(root?), updateCartBadge(count), getCartCount(),
 *   bindSearchHandler(fn), bindAccountHandler(fn), bindCartHandler(fn)
 * }
 *
 * Spec sources:
 *   Header is STATIC (no sticky behavior — kept as a stub for future opt-in).
 *   Cart badge persisted via localStorage('kosho_cart_count').
 *
 * Loaded as ES module via /assets/js/main.js (or directly per page).
 */

(function (global) {
  'use strict';

  // ────────────────────────────────────────────────────────────
  //  Constants
  // ────────────────────────────────────────────────────────────

  const STORAGE_KEY = 'kosho_cart_count';

  const SELECTORS = Object.freeze({
    root:            '[data-component="header"]',
    cartBadge:       '.header__cart-badge',
    cartButton:      '.header__icon-btn--cart',
    searchButton:    '[data-action="open-search"]',
    accountButton:   '[data-action="open-account"]',
    cartActionBtn:   '[data-action="open-cart"]',
    hamburgerBtn:    '[data-action="open-mobile-nav"]',
    currencyBtns:    '.header__currency-item',
  });

  // ────────────────────────────────────────────────────────────
  //  State
  // ────────────────────────────────────────────────────────────

  /** @type {HTMLElement|null} */
  let rootEl = null;
  /** External handler hooks (set via bindXHandler) */
  const handlers = {
    search:  null,
    account: null,
    cart:    null,
  };

  // ────────────────────────────────────────────────────────────
  //  Helpers
  // ────────────────────────────────────────────────────────────

  function $(selector, scope) {
    return (scope || rootEl || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.from((scope || rootEl || document).querySelectorAll(selector));
  }

  function safeStorageGet(key) {
    try { return global.localStorage && global.localStorage.getItem(key); }
    catch (_) { return null; }
  }

  function safeStorageSet(key, value) {
    try { global.localStorage && global.localStorage.setItem(key, String(value)); }
    catch (_) { /* private mode / quota — ignore */ }
  }

  function clampCount(n) {
    const v = Math.max(0, Math.floor(Number(n) || 0));
    return v;
  }

  function formatCount(n) {
    // Cap visible count at 99+ to keep badge geometry stable
    return n > 99 ? '99+' : String(n);
  }

  // ────────────────────────────────────────────────────────────
  //  Cart badge
  // ────────────────────────────────────────────────────────────

  function updateCartBadge(count) {
    const n = clampCount(count);
    const badge = $(SELECTORS.cartBadge);
    if (!badge) return;

    badge.textContent = formatCount(n);
    badge.setAttribute('data-cart-count', String(n));
    badge.setAttribute('aria-label', n + ' item' + (n === 1 ? '' : 's') + ' in cart');

    if (n > 0) badge.removeAttribute('hidden');
    else       badge.setAttribute('hidden', '');

    safeStorageSet(STORAGE_KEY, n);
  }

  function getCartCount() {
    const stored = safeStorageGet(STORAGE_KEY);
    return clampCount(stored);
  }

  // ────────────────────────────────────────────────────────────
  //  Icon click handlers (stubs — bindable from outside)
  // ────────────────────────────────────────────────────────────

  function dispatch(name, detail) {
    rootEl && rootEl.dispatchEvent(
      new CustomEvent('kosho-header:' + name, { bubbles: true, detail: detail || {} })
    );
  }

  function onSearchClick(ev) {
    ev.preventDefault();
    dispatch('open-search');
    if (typeof handlers.search === 'function') handlers.search(ev);
  }

  function onAccountClick(ev) {
    ev.preventDefault();
    dispatch('open-account');
    if (typeof handlers.account === 'function') handlers.account(ev);
  }

  function onCartClick(ev) {
    ev.preventDefault();
    dispatch('open-cart');
    if (typeof handlers.cart === 'function') handlers.cart(ev);
  }

  function bindSearchHandler(fn)  { handlers.search  = fn; }
  function bindAccountHandler(fn) { handlers.account = fn; }
  function bindCartHandler(fn)    { handlers.cart    = fn; }

  // ────────────────────────────────────────────────────────────
  //  Currency switcher (visual state only)
  // ────────────────────────────────────────────────────────────

  function onSwitcherClick(ev) {
    const btn = ev.currentTarget;
    const group = btn.parentElement;
    if (!group) return;

    const activeClass = 'header__currency-item--active';

    $$( '.' + activeClass, group ).forEach(function (el) {
      el.classList.remove(activeClass);
      el.removeAttribute('aria-pressed');
    });
    btn.classList.add(activeClass);
    btn.setAttribute('aria-pressed', 'true');

    const value = btn.getAttribute('data-currency');
    if (value) group.setAttribute('data-active-currency', value);

    dispatch('currency-change', { value: value });
  }

  // ────────────────────────────────────────────────────────────
  //  Sticky behavior — STUB.
  //  Spec says PC header is STATIC. This stub exists so future
  //  opt-in (e.g., on scroll-down hide / scroll-up reveal) is one
  //  flag away. Activate by setting data-sticky="true" on the
  //  header root.
  // ────────────────────────────────────────────────────────────

  function initStickyBehavior() {
    if (!rootEl) return;
    if (rootEl.getAttribute('data-sticky') !== 'true') return;
    // Reserved for future implementation. Intentionally empty.
  }

  // ────────────────────────────────────────────────────────────
  //  Init
  // ────────────────────────────────────────────────────────────

  function init(scope) {
    rootEl = (scope && scope.querySelector
      ? scope.querySelector(SELECTORS.root)
      : document.querySelector(SELECTORS.root));

    if (!rootEl) return false;

    // Wire icon buttons
    const searchBtn  = $(SELECTORS.searchButton);
    const accountBtn = $(SELECTORS.accountButton);
    const cartBtn    = $(SELECTORS.cartActionBtn);

    searchBtn  && searchBtn.addEventListener('click',  onSearchClick);
    accountBtn && accountBtn.addEventListener('click', onAccountClick);
    cartBtn    && cartBtn.addEventListener('click',    onCartClick);

    // Switchers
    $$(SELECTORS.currencyBtns).forEach(function (btn) {
      btn.addEventListener('click', onSwitcherClick);
    });

    // Restore cart badge from localStorage
    updateCartBadge(getCartCount());

    // Sticky behavior (currently a no-op stub)
    initStickyBehavior();

    return true;
  }

  // ────────────────────────────────────────────────────────────
  //  Public API
  // ────────────────────────────────────────────────────────────

  const KoshoHeader = {
    init: init,
    updateCartBadge: updateCartBadge,
    getCartCount: getCartCount,
    bindSearchHandler: bindSearchHandler,
    bindAccountHandler: bindAccountHandler,
    bindCartHandler: bindCartHandler,
  };

  global.KoshoHeader = KoshoHeader;

  // Auto-init on DOM ready when included as a plain <script> (defer ok).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

})(typeof window !== 'undefined' ? window : globalThis);
